import type { ReactNode } from 'react';

/**
 * Regular expression to match valid website URLs in text.
 * Matches http, https protocols and URLs starting with www.
 * Supports domains, ports, paths, query parameters, and fragments.
 */
const URL_REGEX =
    /(?:\b|^)(?:https?:\/\/|www\.)[a-z0-9]+(?:[.-][a-z0-9]+)*\.[a-z]{2,}(?::[0-9]{1,5})?(?:\/[a-z0-9._~%-]*[a-z0-9_~%+]+)*\/?(?:[?#][-a-z0-9._~%=&+:@/?!$'*,;#]*)?(?=\s|\b|$|[.,](?:\s|$))/gi;

/**
 * Converts URLs in plain text to clickable links.
 * Returns an array of React nodes with text and link elements.
 * @param text The plain text content to linkify.
 * @param linkClassName The class name to apply to the links.
 * @returns Array of React nodes with text and links.
 * @example
 * linkifyText('Check out https://example.com')
 * // Returns: ['Check out ', <a href="https://example.com" ...>https://example.com</a>]
 */
export function linkifyText(text: string, linkClassName?: string): ReactNode[] {
    const parts: ReactNode[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    // Reset regex state
    URL_REGEX.lastIndex = 0;

    while ((match = URL_REGEX.exec(text)) !== null) {
        const url = match[0];
        const index = match.index;

        // Add text before the URL
        if (index > lastIndex) {
            parts.push(text.slice(lastIndex, index));
        }

        // Determine the href - add https:// protocol if missing (for www. URLs)
        const href =
            url.startsWith('http://') || url.startsWith('https://')
                ? url
                : `https://${url}`;

        // Add the link element
        parts.push(
            <a
                key={`link-${index}`}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className={linkClassName}
            >
                {url}
            </a>
        );

        lastIndex = index + url.length;
    }

    // Add remaining text after the last URL
    if (lastIndex < text.length) {
        parts.push(text.slice(lastIndex));
    }

    // If no URLs were found, return the original text
    return parts.length > 0 ? parts : [text];
}
